// Exporta tracks (formato interno) a GPX 1.1.
//
// modo 'single'   → UN <trk> con un <trkseg> por ruta. Wikiloc lo importa como
//                   UNA sola ruta; los dispositivos la ven como un track con
//                   tramos, sin líneas rectas artificiales entre rutas.
//                   Va ENRIQUECIDO: índice de rutas en <desc>, un <wpt> en el
//                   inicio de cada ruta original, y metadatos por <trkseg>
//                   (extensiones con namespace propio) que permiten a Querencia
//                   reconstruir las rutas al reimportar el archivo.
// modo 'separate' → un <trk> por ruta (Wikiloc obliga a subirlas una a una).
//
// includeTime: por defecto false en 'single' (un agregado no es una grabación
// temporal real; mezclar timestamps de días distintos confunde a los visores).

import { IMPORTED_LABEL } from './owners.js';

export const QUERENCIA_XMLNS = 'https://querencia.zigiella.com/xmlns/1';

function escapeXml(s) {
  return String(s ?? '').replace(/[<>&'"]/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c]),
  );
}

// Propietario "de verdad" (la etiqueta interna "Importado" no viaja).
function realOwner(track) {
  return track.ownerLabel && track.ownerLabel !== IMPORTED_LABEL ? track.ownerLabel : '';
}

// Línea de índice de una ruta: "1. Nombre · 10.4 km · Propietario"
function manifestLine(track, i) {
  const owner = realOwner(track);
  return `${i + 1}. ${track.name || 'Ruta'} · ${(track.distanceKm ?? 0).toFixed(1)} km${owner ? ` · ${owner}` : ''}`;
}

// Metadatos del track de tracks en UN bloque a nivel de <trk> (orden del
// esquema GPX: tras <desc>, antes de los <trkseg>). Los <trkseg> quedan
// idénticos a un GPX corriente: solo <trkpt>, sin contenido inesperado que
// pueda confundir a parsers ajenos. Querencia los lee al reimportar; el
// orden de las entradas identifica cada ruta y `segs` cuántos tramos aporta.
function trkMetaXml(tracks) {
  const entries = tracks
    .map((t) => {
      const owner = realOwner(t);
      const geom = t.feature?.geometry;
      const segs = geom?.type === 'MultiLineString' ? geom.coordinates.length : 1;
      return `<querencia:trk>` +
        `<querencia:name>${escapeXml(t.name || '')}</querencia:name>` +
        (owner ? `<querencia:owner>${escapeXml(owner)}</querencia:owner>` : '') +
        (segs > 1 ? `<querencia:segs>${segs}</querencia:segs>` : '') +
        `</querencia:trk>`;
    })
    .join('');
  return `<extensions>${entries}</extensions>`;
}

function trkptXml(coord, time) {
  const [lon, lat, ele] = coord;
  let body = '';
  if (Number.isFinite(ele)) body += `<ele>${ele.toFixed(2)}</ele>`;
  if (time) body += `<time>${escapeXml(time)}</time>`;
  return body
    ? `<trkpt lat="${lat}" lon="${lon}">${body}</trkpt>`
    : `<trkpt lat="${lat}" lon="${lon}"/>`;
}

function segmentsXml(track, includeTime, segExtra = '') {
  const geom = track.feature?.geometry;
  if (!geom) return '';
  const times = includeTime ? track.feature?.properties?.coordTimes : null;
  // Soporta LineString y MultiLineString
  const lines = geom.type === 'MultiLineString' ? geom.coordinates : [geom.coordinates];
  return lines
    .map((coords) => {
      const pts = coords.map((c, i) => trkptXml(c, times?.[i])).join('');
      return `<trkseg>${pts}${segExtra}</trkseg>`;
    })
    .join('');
}

export function tracksToGPX(tracks, { name = 'Querencia', mode = 'single', includeTime = false, manifestHeader = '' } = {}) {
  const stamp = new Date().toISOString();
  const manifest = [manifestHeader, ...tracks.map((t, i) => manifestLine(t, i))].filter(Boolean).join('\n');
  const header = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Querencia" xmlns="http://www.topografix.com/GPX/1/1" xmlns:querencia="${QUERENCIA_XMLNS}" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
<metadata>
  <name>${escapeXml(name)}</name>
  <time>${stamp}</time>
  <desc>${escapeXml(manifest)}</desc>
</metadata>`;

  let body;
  if (mode === 'separate') {
    body = tracks
      .map((t) => {
        const src = t.ownerLabel ? `<src>${escapeXml(t.ownerLabel)}</src>` : '';
        return `<trk><name>${escapeXml(t.name || 'Track')}</name>${src}${segmentsXml(t, includeTime)}</trk>`;
      })
      .join('\n');
  } else {
    // single enriquecido: un trk con índice de rutas en <desc>, metadatos
    // reimportables en <extensions> a nivel de trk, y un trkseg por ruta
    // IDÉNTICO al formato clásico (solo trkpt: ningún visor tiene motivo
    // para tratar los tramos distinto que antes). Sin waypoints: al subirlo
    // a Wikiloc confunden más de lo que aportan (decisión 2026-07-03).
    const segs = tracks.map((t) => segmentsXml(t, includeTime)).join('');
    body = `<trk><name>${escapeXml(name)}</name><desc>${escapeXml(manifest)}</desc>${trkMetaXml(tracks)}${segs}</trk>`;
  }

  return `${header}
${body}
</gpx>`;
}

export function downloadGPX(filename, gpxText) {
  const blob = new Blob([gpxText], { type: 'application/gpx+xml' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function safeFilename(s) {
  return String(s || 'querencia-export')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, '_')
    .slice(0, 60);
}
